import React, { Component, ReactNode } from 'react';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { MatchDetailsHeaderBasePublicProps } from '@om-fe/core/components/match-details/utils/match-details-header.utils';
import { BemMethod, createBem, DateFormatType, preventEvent } from '@om-fe/utils';
import { MatchDetailsViewModel } from '@om-fe/models';
import { MatchDetailsSliderWidgetIds } from '@om-fe/widgets/dist/widgets/match-details-slider/match-details-slider.constants';
import { DateFormat } from '@om-fe/widgets/dist/components/date-format/date-format';
import { ParticipantIcon } from '@om-fe/widgets/dist/components/icon/participant-icon';
import { WidgetType } from '@om-fe/widgets/dist/components/external-sport-widgets/widget.constants';
import { BrowserOnly } from '@om-fe/widgets/dist/components/ssr/browser-only';
import { StatScoreWidgetV2 } from '@om-fe/widgets/components/stat-score/statscore-widget';
import { getEventMapping, EventMappingRpcResponseItem } from '@om-fe/api';
import BetRadarStatistics from '@om-fe/core/components/betradar-statistics/betradar-statistics';
import isEmpty from 'lodash/isEmpty';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import getConfig, { OMFE2ConfigModel, statScoreV2Item, IconSize, LayoutConfig } from '@om-fe/core/utils/config';
import FavoriteTeamsButton from '@om-fe/core/components/favorite-teams/favorite-teams-button';

export type MatchDetailsHeaderPublicProps = MatchDetailsHeaderBasePublicProps;

const matchDetailsHeaderBem: BemMethod = createBem('MatchDetailsHeader');
const bem: BemMethod = createBem('MatchTime');

type MatchDetailsHeaderProps = {
    readonly item?: MatchDetailsViewModel;
    readonly breakpoints: Breakpoints;
    readonly activeTab?: MatchDetailsSliderWidgetIds;
};

type MatchDetailsHeaderState = {
    betRadarStatistics: WidgetType.Betradar | WidgetType.BetradarLMT,
    statScoreStatistics: WidgetType.StatScoreV2,
    eventMappingReady: boolean,
}

export type MatchDetailsHeaderDefaultProps =
    MatchDetailsHeaderPublicProps
    & MatchDetailsHeaderProps
    & MatchDetailsHeaderState;

class MatchDetailsHeaderComponent extends Component<MatchDetailsHeaderDefaultProps> {
    state = {
        betRadarStatistics: null,
        statScoreStatistics: null,
        betRadarWidget: null,
        eventMappingReady: false,
    }

    async checkEventWidget(): Promise<void> {
        const statScoreConfig: statScoreV2Item | undefined = getConfig().statScoreWidgetV2Config?.group;
        const { item, selectedLanguage } = this.props;
        const matchId = item.eventId;

        if (!matchId || !selectedLanguage) {
            return;
        }

        const eventMapping = await getEventMapping({
            lang: selectedLanguage,
            matchId,
        });

        if (eventMapping.STATS && !isEmpty(eventMapping.STATS)) {
            if (statScoreConfig) {
                const eventStatistics: EventMappingRpcResponseItem | undefined =
                    eventMapping.STATS.find(
                        (widget: EventMappingRpcResponseItem) =>
                        (widget.key === WidgetType.StatScoreV2)
                    );
                this.setState({
                    statScoreStatistics: eventStatistics,
                    eventMappingReady: true,
                });
            } else {
                const eventStatistics: EventMappingRpcResponseItem | undefined =
                eventMapping.STATS.find(
                    (widget: EventMappingRpcResponseItem) =>
                    (widget.key === WidgetType.Betradar || widget.key === WidgetType.BetradarLMT)
                );
                this.setState({
                    betRadarStatistics: eventStatistics,
                    eventMappingReady: true,
                });
            }
        } else {
            this.setState({
                betRadarStatistics: undefined,
                tatScoreStatistics: undefined,
                eventMappingReady: true,
            });
        }
        if (eventMapping.SCORE && !isEmpty(eventMapping.SCORE)) {
            const eventBrWidget: EventMappingRpcResponseItem | undefined =
                eventMapping.SCORE.find(
                    (widget: EventMappingRpcResponseItem) =>
                    (widget.key === WidgetType.BrWidget || widget.key === WidgetType.BrWidgetLMT)
                );
            this.setState({
                betRadarWidget: eventBrWidget,
            });
        } else {
            this.setState({ betRadarWidget: undefined });
        }
    }

    async componentDidMount() {
        await this.checkEventWidget();
    }

    async componentDidUpdate() {
        const { item, selectedLanguage } = this.props;
        const matchId = item.eventId;

        if (!matchId || !selectedLanguage) {
            this.checkEventWidget()
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                });
        }
    }

    getParticipantIcon(participantLogoUrl: string, participantClass?: string): ReactNode {
        const { item } = this.props;

        if (!item) {
            return;
        }

        return (
            <span className={matchDetailsHeaderBem('ParticipantIconWrapper', { Home: participantClass === 'Home', Away: participantClass === 'Away' })}>
                <ParticipantIcon
                    logoUrl={participantLogoUrl}
                    disciplineId={item.sportId}
                    size={IconSize.Large1}
                />
            </span>
        );
    }

    render() {
        const {
            item,
            breakpoints,
            selectedLanguage,
            activeTab,
        } = this.props;

        if (!item) {
            return null;
        }

        const config: OMFE2ConfigModel = getConfig();
        const layoutConfig: LayoutConfig | undefined = config.layout;
        const statScoreConfig: statScoreV2Item | undefined = config.statScoreWidgetV2Config?.group;
        const breakpointSmall: boolean = breakpoints.small;
        const { betRadarStatistics, betRadarWidget, statScoreStatistics, eventMappingReady } = this.state;
        const dateFormat: string | undefined = config.matchPageConfig?.dateFormat?.matchDetailsHeader;
        const enabledRightSideBarLMT: boolean = config.betradar?.enabledRightSideBarLMT ?? false;

        const {
            homeName,
            awayName,
            disciplineId,
            locationName,
            tournamentName,
            startTime,
            homeParticipantLogoUrl,
            awayParticipantLogoUrl,
            homeId,
            awayId,
        } = item;

        if (!enabledRightSideBarLMT && breakpointSmall && (betRadarStatistics === null || betRadarStatistics || betRadarWidget === null || betRadarWidget)) {
            return null;
        }

        const participantLogoHome: ReactNode | null = layoutConfig?.enableEventParticipantLogo && homeParticipantLogoUrl ? this.getParticipantIcon(homeParticipantLogoUrl, 'Home') : null;
        const participantLogoAway: ReactNode | null = layoutConfig?.enableEventParticipantLogo && awayParticipantLogoUrl ? this.getParticipantIcon(awayParticipantLogoUrl, 'Away') : null;

        return (
            enabledRightSideBarLMT && activeTab && activeTab !== MatchDetailsSliderWidgetIds.MatchDetailsHeader
            ? null
            :
            <section className={matchDetailsHeaderBem('', { [`Sport-${disciplineId}`]: !!disciplineId })}>
                <div className={matchDetailsHeaderBem('TopSection')}>
                    {(!statScoreConfig || (!statScoreStatistics && eventMappingReady && statScoreConfig)) && (
                        <div className={matchDetailsHeaderBem('HomeAwayWrapper')}>
                            <div className={matchDetailsHeaderBem('Time')}>
                                <div className={matchDetailsHeaderBem('TimeWrapper')}>
                                    <span className={bem('InfoPart', 'Date')}>
                                        <DateFormat date={startTime} pattern={dateFormat ?? 'dddd, DD MMMM'} />
                                    </span>
                                    <span className={bem('InfoPart', 'Time')}>
                                        <DateFormat date={startTime} pattern={DateFormatType.FormatHour} />
                                    </span>
                                </div>
                                <div className={matchDetailsHeaderBem('Place')}>
                                    <span className={matchDetailsHeaderBem('Place', 'Info')}>
                                        {locationName} - {tournamentName}
                                    </span>
                                </div>
                            </div>
                            <div className={matchDetailsHeaderBem('Participants')}>
                                {participantLogoHome}
                                <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                    <FavoriteTeamsButton participantId={homeId} />
                                </div>
                                <span className={matchDetailsHeaderBem('PartName', { Home: true })}>
                                    {homeName}
                                </span>
                                <span className={matchDetailsHeaderBem('PartSeparator')}>vs</span>
                                <div className={bem('FavoriteButton')} onClick={preventEvent}>
                                    <FavoriteTeamsButton participantId={awayId} />
                                </div>
                                <span className={matchDetailsHeaderBem('PartName', { Away: true })}>
                                    {awayName}
                                </span>
                                {participantLogoAway}
                            </div>
                        </div>
                    )}
                    {statScoreConfig && statScoreStatistics && (statScoreStatistics as unknown as { value: string })?.value
                        ?
                            <BrowserOnly>
                                <div className={matchDetailsHeaderBem('StatScoreStatistics')}>
                                    <StatScoreWidgetV2
                                        statscoreWidgetId={statScoreStatistics.value}
                                        configurationId={statScoreConfig.pending}
                                        lang={selectedLanguage}
                                        statScoreWidgetV2Group={true}
                                    />
                                </div>
                            </BrowserOnly>
                        : null
                    }
                    {betRadarStatistics &&
                        <div className={matchDetailsHeaderBem('StatisticsButton')}>
                            <BetRadarStatistics betRadarEventId={betRadarStatistics.value}/>
                        </div>
                    }
                </div>
            </section>
        );
    }
}

const MatchDetailsHeader: React.ComponentClass<MatchDetailsHeaderPublicProps> =
    withTranslation<MatchDetailsHeaderPublicProps>()(MatchDetailsHeaderComponent);
export default MatchDetailsHeader;
